import {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {ButtonWithIcon} from '@/components/atoms';
import {Accordion} from '@/components/molecules/Accordion';
import {AccordionItem} from '@/components/molecules/AccordionItem';
import PerfectStoreWizardLayout from '@/containers/PerfectStore/PerfectStoreWizardLayout';
import {
  PerfectStoreSetupReview,
  PerfectStoreDisplayReview
} from '@/containers/PerfectStore/PerfectStoreReview';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetPerfectStore,
  setPerfectStoreFormData,
  setPerfectStoreMeta
} from '@/store/slices/perfectStoreSlice';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  getPerfectStoreCancelModal,
  isPerfectStoreDisplayInvalid,
  isPerfectStoreSetupInvalid,
  mapPerfectStoreApiToFormData
} from '@/pages/PerfectStoreUtils';
import {usePerfectStore} from '@/hooks/usePerfectStore';
import {getPerfectStoreDetail} from '@/services/perfectStore';
import {Constants} from '@/utils/constants/commonConstants';
import {logger} from '@/helpers/logger';
import '@/containers/PerfectStore/PerfectStoreReview/perfectStoreReview.scss';

const EditSectionButton = ({step, onEdit}) => (
  <ButtonWithIcon
    iconLeading='edit'
    handleButton={(event) => {
      event?.stopPropagation();
      onEdit(step);
    }}
    uiSchema={{
      title: 'Edit Section',
      variant: 'tertiary',
      size: 'small',
      isDark: true
    }}
  />
);

EditSectionButton.propTypes = {
  step: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired
};

export const PerfectStoreReview = () => {
  const {id} = useParams();
  const location = useLocation();
  const disableDraftFromState = location?.state?.disableDraft ?? false;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const formData = useAppSelector((state) => state?.perfectStore?.formData);
  const perfectStoreStatus = useAppSelector(
    (state) => state?.perfectStore?.status
  );
  const countryCode = useAppSelector(selectCountryCode);
  const commonLanguages = useAppSelector((state) => state.common?.languages);
  const [isCancel, setIsCancel] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(!!id);
  const {handleDraft, handlePublish} = usePerfectStore();

  const isPublishedDetail =
    !!id &&
    String(perfectStoreStatus || formData?.status || '').toLowerCase() ===
      Constants.Published.toLowerCase();
  const disableDraft = disableDraftFromState || isPublishedDetail;

  useEffect(() => {
    if (!id) return undefined;

    let cancelled = false;

    const loadPerfectStoreDetail = async () => {
      setIsLoadingDetail(true);
      try {
        const response = await getPerfectStoreDetail(id);
        const detail =
          response?.data?.content?.[0] ??
          response?.data ??
          response?.content?.[0] ??
          response;

        if (cancelled || !detail || typeof detail !== 'object') {
          return;
        }

        dispatch(setPerfectStoreFormData(mapPerfectStoreApiToFormData(detail)));
        dispatch(
          setPerfectStoreMeta({
            id: detail.id ?? id,
            uuid: detail.uuid ?? null,
            status: detail.status ?? null
          })
        );
      } catch (error) {
        logger.error('Error loading Perfect Store detail:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingDetail(false);
        }
      }
    };

    loadPerfectStoreDetail();

    return () => {
      cancelled = true;
    };
  }, [dispatch, id]);

  const languages = useMemo(
    () => COUNTRY_LANGUAGES_CODES[countryCode] || commonLanguages || [],
    [countryCode, commonLanguages]
  );

  const getNavigationState = () => ({
    isEdit: true,
    disableDraft,
    fromWizard: true
  });

  const handleStepClick = (step) => {
    const stepRoutes = {
      1: ROUTES.PERFECT_STORE_ADD,
      2: ROUTES.PERFECT_STORE_DISPLAY
    };
    if (stepRoutes[step]) {
      navigate(stepRoutes[step], {state: getNavigationState()});
    }
  };

  const handleCancel = () => setIsCancel(!isCancel);

  const onClickPrimaryButton = () => {
    dispatch(resetPerfectStore());
    navigate(ROUTES.PERFECT_STORE);
  };

  const handleDraftAction = async () => {
    try {
      const response = await handleDraft(formData);
      if (response?.success) {
        navigate(ROUTES.PERFECT_STORE);
      }
    } catch (error) {
      logger.error('Error saving Perfect Store draft:', error);
    }
  };

  const nextAction = async () => {
    try {
      const response = await handlePublish(formData);
      if (response?.success) {
        navigate(ROUTES.PERFECT_STORE);
      }
    } catch (error) {
      logger.error('Error publishing Perfect Store:', error);
    }
  };

  return (
    <>
      {isLoadingDetail ? (
        <p data-testid='perfect-store-review-loading'>Loading...</p>
      ) : (
        <PerfectStoreWizardLayout
          currentStep={3}
          title='Create Perfect Store'
          onStepClick={handleStepClick}
          FormError={
            isPerfectStoreSetupInvalid(formData) ||
            isPerfectStoreDisplayInvalid(formData, languages)
          }
          handleCancel={handleCancel}
          draftAction={handleDraftAction}
          nextAction={nextAction}
          hideSaveDraft={false}
          disableDraft={disableDraft}
          isReview>
          <div className='perfect-store-review flex flex-col gap-4'>
            <Accordion
              separator
              content={
                <AccordionItem
                  expanded
                  label='Setup'
                  rightAccessoryChild={
                    <EditSectionButton step={1} onEdit={handleStepClick} />
                  }
                  content={<PerfectStoreSetupReview formData={formData} />}
                />
              }
            />
            <Accordion
              separator
              content={
                <AccordionItem
                  expanded
                  label='Display'
                  rightAccessoryChild={
                    <EditSectionButton step={2} onEdit={handleStepClick} />
                  }
                  content={
                    <PerfectStoreDisplayReview
                      formData={formData}
                      languages={languages}
                    />
                  }
                />
              }
            />
          </div>
        </PerfectStoreWizardLayout>
      )}
      {isCancel &&
        getPerfectStoreCancelModal({
          show: isCancel,
          onConfirm: onClickPrimaryButton,
          onClose: handleCancel
        })}
    </>
  );
};
